import React, { useState, useMemo, useRef, useEffect } from 'react';
import ElencoDomande from './pazienti/ElencoDomande';
import '../styles/QuestionarioTablet.css';
import intestazioniTablet from '../utils/chiaveDispositivo';

/**
 * QuestionarioTablet — il paziente risponde, sul tablet.
 *
 * PERCHE' A PAGINE E NON TUTTO IN UNA SCHERMATA
 * Il piu' lungo dei cinque strumenti ha 84 item. Una pagina sola diventa
 * un rotolo in cui si perde il segno e si salta una riga senza
 * accorgersene; una domanda per schermata, all'opposto, sono 84 tocchi di
 * «avanti» e la gente si stanca e risponde a caso verso la fine. Otto per
 * pagina e' il compromesso: si vede dove si e' arrivati, e la scala delle
 * risposte resta la stessa per tutta la pagina.
 *
 * NON SI PUO' ANDARE AVANTI LASCIANDO UN BUCO, MA SI PUO' FINIRE CON UN BUCO
 * Il pulsante «avanti» chiede le risposte della pagina, perche' la
 * dimenticanza piu' comune e' saltare una riga scorrendo. Ma alla fine,
 * se qualcosa e' rimasto in bianco, si consegna lo stesso segnalando cosa
 * manca: un questionario incompleto e' un dato, un questionario che il
 * paziente non e' riuscito a chiudere e' niente. Chi legge in cartella
 * vede quali item mancano.
 *
 * UNA PARTE SOLA, QUANDO IL QUESTIONARIO E' A QUATTRO MANI
 * La scheda gastroenterologica ha 166 campi: dalla sezione 12 in poi
 * sono referti, esami e piano clinico, che li compila il medico. Al
 * tablet arrivano SOLO le sezioni del paziente - il taglio lo fa il
 * server, quindi il resto non transita nemmeno - e la copertina dice che
 * si sta compilando una parte, non tutto. Chi crede di aver finito un
 * documento e scopre che ne mancava meta' si sente preso in giro.
 *
 * L'ANAGRAFICA NON SI CHIEDE
 * Data, codice paziente, data di nascita, sesso: i moduli cartacei li
 * hanno in testa, qui arrivano dalla scheda. In sala d'attesa non si fa
 * ribattere il codice fiscale a chi sta per rispondere a domande sul
 * proprio umore.
 */

const PER_PAGINA = 8;

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const QuestionarioTablet = ({ chiave, sessione, onFatto }) => {
    const q = sessione.questionario;
    const def = q?.definizione || {};
    const item = useMemo(() => def.item || [], [def]);
    const scale = useMemo(
        () => new Map((def.scale_risposta || []).map((s) => [s.id, s])),
        [def]
    );

    /* Se il questionario era stato iniziato alla postazione, si riparte da
       li': il paziente vede cio' che ha gia' risposto col medico, e l'invio
       non cancella quel lavoro. */
    const [risposte, setRisposte] = useState(() => sessione.risposte_iniziali || {});
    /* Si riparte dalla prima pagina con una domanda senza risposta: chi
       riapre il tablet a meta' non deve rifare a ritroso tutto quello che
       ha gia' fatto (26/09/2026). */
    const [pagina, setPagina] = useState(() => {
        const date = sessione.risposte_iniziali || {};
        const tutti = (sessione.questionario?.definizione?.item) || [];
        const primo = tutti.findIndex((it) => date[String(it.n)] === undefined);
        return primo > 0 ? Math.floor(primo / PER_PAGINA) : 0;
    });
    /* Ogni risposta si mette da parte (migration 254), un attimo dopo
       l'ultimo tocco: se la pagina si chiude, riaprendola le risposte ci
       sono. Se il salvataggio non riesce si va avanti lo stesso. */
    const ultimeSalvate = useRef(JSON.stringify(sessione.risposte_iniziali || {}));
    useEffect(() => {
        const testo = JSON.stringify(risposte);
        if (testo === ultimeSalvate.current) return undefined;
        const t = setTimeout(() => {
            fetch('/api/firma-tablet/risposte', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify({ codice: sessione.codice, risposte }),
            }).then((r) => { if (r.ok) ultimeSalvate.current = testo; }).catch(() => {});
        }, 400);
        return () => clearTimeout(t);
    }, [risposte, chiave, sessione.codice]);
    const [avviato, setAvviato] = useState(false);
    const [mancanti, setMancanti] = useState([]);
    const [confermaVuoti, setConfermaVuoti] = useState(false);
    const [invio, setInvio] = useState(false);
    const [errore, setErrore] = useState(null);
    const alto = useRef(null);

    const pagine = Math.max(1, Math.ceil(item.length / PER_PAGINA));
    const diQuestaPagina = item.slice(pagina * PER_PAGINA, (pagina + 1) * PER_PAGINA);
    const date = Object.keys(risposte).length;

    const rispondi = (n, valore) => {
        setRisposte((p) => ({ ...p, [String(n)]: valore }));
        setMancanti((m) => m.filter((x) => x !== n));
    };

    const vaiA = (p) => {
        setPagina(p);
        setMancanti([]);
        // Cambiando pagina si torna in cima: senza, si riparte da meta'
        // schermata e le prime domande sembrano non esserci.
        if (alto.current) alto.current.scrollTo({ top: 0, behavior: 'auto' });
    };

    const avanti = () => {
        const vuoti = diQuestaPagina
            .filter((it) => risposte[String(it.n)] === undefined)
            .map((it) => it.n);
        if (vuoti.length) { setMancanti(vuoti); return; }
        vaiA(pagina + 1);
    };

    const consegna = async (anchePerVuoti = false) => {
        const vuoti = item.filter((it) => risposte[String(it.n)] === undefined).map((it) => it.n);
        if (vuoti.length && !anchePerVuoti) {
            setConfermaVuoti(true);
            return;
        }
        try {
            setInvio(true);
            setErrore(null);
            const r = await fetch('/api/firma-tablet/questionario', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify({ codice: sessione.codice, risposte }),
            });
            const d = await r.json();
            if (!d.success) { setErrore(d.message || 'Invio non riuscito'); return; }
            onFatto(d.data);
        } catch (e) {
            setErrore('Errore di collegamento. Riprovare.');
        } finally {
            setInvio(false);
        }
    };

    if (!item.length) {
        return (
            <div className="qt-esito">
                <h1>Questionario non disponibile</h1>
                <p>Riconsegni il dispositivo alla segreteria.</p>
            </div>
        );
    }

    /* ---------- copertina ---------- */
    if (!avviato) {
        return (
            <div className="qt-copertina">
                <h1>{q.titolo_visualizzato || q.nome}</h1>
                <div className="qt-chi">
                    {sessione.paziente_nome} {sessione.paziente_cognome}
                    <span>nato il {dataIta(sessione.data_nascita)} · {sessione.id_paziente}</span>
                </div>
                {q.istruzioni && <p className="qt-istruzioni">{q.istruzioni}</p>}
                <p className="qt-quante">
                    {item.length} domande, {pagine} {pagine === 1 ? 'pagina' : 'pagine'}.
                    Non ci sono risposte giuste o sbagliate.
                </p>
                {sessione.parziale && (
                    <p className="qt-parziale">
                        Sono le domande che riguardano lei: quello che sente, come
                        mangia, come sta. La parte con gli esami e la visita la
                        compila il medico.
                    </p>
                )}
                <button className="qt-btn qt-btn--grande" onClick={() => setAvviato(true)}>
                    Comincia
                </button>
            </div>
        );
    }

    const ultima = pagina === pagine - 1;

    return (
        <div className="qt-wrap" ref={alto}>
            <div className="qt-testata">
                <span className="qt-titolo">{q.titolo_visualizzato || q.nome}</span>
                <span className="qt-avanzamento">{date} di {item.length}</span>
            </div>
            <div className="qt-barra">
                <span style={{ width: `${(date / item.length) * 100}%` }} />
            </div>

            <div className="qt-corpo">
                {mancanti.length > 0 && (
                    <p className="qt-avviso">
                        {mancanti.length === 1
                            ? 'Manca una risposta in questa pagina.'
                            : `Mancano ${mancanti.length} risposte in questa pagina.`}
                    </p>
                )}

                <ElencoDomande item={diQuestaPagina} scale={scale} risposte={risposte}
                               onRispondi={rispondi} mancanti={mancanti} />
            </div>

            {confermaVuoti && (
                <div className="qt-conferma">
                    <p>
                        <strong>
                            {item.length - date === 1
                                ? 'È rimasta una domanda senza risposta.'
                                : `Sono rimaste ${item.length - date} domande senza risposta.`}
                        </strong>{' '}
                        Può tornare indietro a completarle, oppure consegnare così:
                        le domande lasciate in bianco risulteranno tali.
                    </p>
                    <div className="qt-azioni">
                        <button className="qt-btn" onClick={() => { setConfermaVuoti(false); vaiA(0); }}>
                            Torno indietro
                        </button>
                        <button className="qt-btn qt-btn--principale"
                                onClick={() => consegna(true)} disabled={invio}>
                            Consegno così
                        </button>
                    </div>
                </div>
            )}

            {errore && <p className="qt-errore">{errore}</p>}

            <div className="qt-piede">
                <button className="qt-btn" onClick={() => vaiA(pagina - 1)}
                        disabled={pagina === 0 || invio}>
                    Indietro
                </button>
                <span className="qt-pagina">Pagina {pagina + 1} di {pagine}</span>
                {ultima ? (
                    <button className="qt-btn qt-btn--principale"
                            onClick={() => consegna(false)} disabled={invio}>
                        {invio ? 'Invio…' : 'Ho finito'}
                    </button>
                ) : (
                    <button className="qt-btn qt-btn--principale" onClick={avanti} disabled={invio}>
                        Avanti
                    </button>
                )}
            </div>
        </div>
    );
};

export default QuestionarioTablet;
